import { GearIcon } from '@radix-ui/react-icons'
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table'
import React, { useEffect } from 'react'

import { partnersSettings } from 'rails_routes'
import { ButtonLink } from 'slab'

import ReferralPageLinkButton from './ReferralPageLinkButton'
import CopyButton from '../reusable/CopyButton'
import TanstackTable from '../reusable/table/TanstackTable'

export type ReferralPageViewData = {
  time: string,
  ip: string,
  referrer: string,
  device: string,
  location: string,
}

const columnHelper = createColumnHelper<ReferralPageViewData>()

const columns = [
  columnHelper.accessor('time', {
    header: 'Viewed at',
    cell: (row) => new Date(row.getValue()).toLocaleString(),
  }),
  columnHelper.accessor('ip', {
    header: 'IP address',
  }),
  columnHelper.accessor('location', {
    header: 'Location',
  }),
  columnHelper.accessor('referrer', {
    header: 'Source',
  }),
  columnHelper.accessor('device', {
    header: 'Device',
  }),
]

function ReferralPageViewsTable ({ publicPageURL, showEditButton }: { publicPageURL: string, showEditButton: boolean }) {
  const [data, setData] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(false)

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  useEffect(() => {
    const fetchReferralPageViews = () => {
      fetch('/api/broker/referral_page_views', { // eslint-disable-line thatch/no-bare-fetch
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      })
        .then(response => response.json())
        .then(json => {
          setData(json.data.events)
          table.setPageSize(20)
        })
        .catch(() => setError(true))
        .finally(() => setLoading(false))
    }
    fetchReferralPageViews()
  }, [table])

  return (
    <div className='w-full h-full flex flex-col'>
      <div className='flex flex-row items-start justify-between flex-wrap gap-3 mb-6'>
        <h1 className='heading-lg'>
          Referral Page
        </h1>
      </div>
      <div className='flex flex-col space-y-2'>
        <div className='flex space-x-2'>
          <div className='border rounded-lg font-mono text-xs flex items-center justify-center py-2 px-4 bg-white'>
            {publicPageURL}
          </div>
          <CopyButton
            valueToCopy={publicPageURL}
            onClickCallback={() => {
              window.segment?.track('[TfB] Referral page link copied')
            }}
          />
          <ReferralPageLinkButton publicPageURL={publicPageURL} />
          {showEditButton && (
            <ButtonLink
              href={partnersSettings.show({ query: { tab: 'referral-page-details' } })}
              variant='tertiary'
              leftIcon={GearIcon}
              onClick={() => { window.segment?.track('[TfB] Edit page details button clicked') }}
            >
              <span className='ml-1'>Edit page details</span>
            </ButtonLink>
          )}
        </div>
      </div>
      <div className='text-sm mb-4 mt-8'>
        Share your referral page with clients. When they sign up, they’ll be added to your client list automatically.
      </div>
      <div className='text-sm mb-4 mt-4'>
        Below are the views on your referral page:
      </div>
      <div className='flow-root'>
        <div>
          <TanstackTable
            table={table}
            isLoading={loading}
            isError={error}
            paginationLabelSingular='view'
            noDataMessage='You don’t have any views on your public agency page yet.'
          />
        </div>
      </div>
    </div>
  )
}

export default ReferralPageViewsTable
